import { isPlainObject } from 'lodash-es';
import { normalizeDateTimePrecision, normalizeMarkets } from './utils';

function isStringOrPlainObject(v: unknown): v is string | Record<string, unknown> {
  return typeof v === 'string' || isPlainObject(v);
}

/**
 * Normalizes payloads into a consistent shape before comparison by:
 * 1) dispatching on runtime value type,
 * 2) applying type-specific normalization rules,
 * 3) recursively normalizing nested values.
 *
 * Why needed: backend and form payloads can be semantically equivalent but shaped
 * differently (key order, timestamp precision, array ordering).
 *
 * @example
 * normalizeComparablePayload({
 *   Markets: [{ MarketAccount: 'coinbase/alice' }, { MarketAccount: 'b2c2/b2c2' }],
 *   TransactTime: '2026-02-20T15:55:29.052871Z',
 * });
 * // => { Markets: ['b2c2/b2c2', 'coinbase/alice'], TransactTime: '2026-02-20T15:55:29.052Z' }
 */
export function normalizeComparablePayload(value: unknown, path = ''): unknown {
  // Normalize Date objects to stable ISO string representation.
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Normalize strings (including timestamp precision).
  if (typeof value === 'string') {
    return normalizeDateTimePrecision(value);
  }

  // Normalize arrays either as markets or as recursively normalized/sorted nested collections.
  if (Array.isArray(value)) {
    const isMarketsField = path === 'Markets' || path.endsWith('.Markets');
    if (isMarketsField) {
      return normalizeMarkets(value.filter(isStringOrPlainObject));
    }

    return value.map(item => normalizeComparablePayload(item, `${path}[]`));
  }

  // Primitive scalars (number/boolean/null/etc.) are already comparable as-is.
  if (!isPlainObject(value)) {
    return value;
  }

  // Recursively normalize object values after removing undefined keys and sorting keys.
  const normalizedEntries = Object.entries(value as Record<string, unknown>)
    .filter(([, nestedValue]) => nestedValue !== undefined)
    .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, nestedValue]) => [key, normalizeComparablePayload(nestedValue, path ? `${path}.${key}` : key)]);

  return Object.fromEntries(normalizedEntries);
}
